import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { createStructuredSelector } from 'reselect';
import { get } from 'lodash';
import moment from 'moment-timezone';
import { locationsApis, referenceApis } from '../../apis';
import { REFERENCE_NAME } from '../Order/constants';
import { BasketSelectors } from '../../redux/basket';
import { SHIPMENT_FIELDS } from '../../constants/forms';
import { fetchCollectionDates } from '../../redux/appActions';
import { DATE_TIME_FORMAT } from '../../constants/dateFormat';
import { NetworkHelpers } from '../../helpers';

const initialState = {
  references: {},
  checkoutList: [],
};

const fetchBasketItemReferences = createAsyncThunk(
  'ui/basketView/fetchBasketItemReferences',
  async basketItem => {
    const { shipment } = basketItem;
    const references = {};
    const {
      _dropOffDetails,
      pickupDetails,
      networkCode,
      deliveryDetails,
      _network,
    } = shipment.outboundConsignment;
    const { busZone } = _network;
    if (pickupDetails?.pickupLocationCode) {
      const { data } = await locationsApis.getPickupLocationsById(
        pickupDetails.pickupLocationCode
      );

      references[REFERENCE_NAME.DELIVERY_PICKUP] = {
        pickupLocation: data,
      };
    }

    if (_dropOffDetails?.pickupLocationCode) {
      const { data } = await locationsApis.getPickupLocationsById(
        _dropOffDetails.pickupLocationCode
      );

      references[REFERENCE_NAME.COLLECTION_PICKUP] = {
        pickupLocation: data,
      };
    }

    if (networkCode) {
      const { data } = await referenceApis.fetchNetworkPrice({
        busZone,
        networkCode,
        countryCode: deliveryDetails.address.countryCode,
      });
      references.networkPrice = data;

      const networksQuery = NetworkHelpers.getNetworkQuery(shipment);
      const { data: networks } = await referenceApis.fetchNetworks(
        networksQuery
      );
      references.networks = networks;
    }
    return [basketItem.basketItemId, references];
  }
);

const fetchBasketReferences = createAsyncThunk(
  'ui/basketView/fetchBasketReferences',
  async (basketId, { dispatch, getState }) => {
    const result = {};
    const basketItems = BasketSelectors.getBasketItems(getState());
    const promises = basketItems.map(async basketItem => {
      const [basketItemId, references] = await dispatch(
        fetchBasketItemReferences(basketItem)
      ).unwrap();
      result[basketItemId] = references;
    });
    await Promise.all(promises);
    return result;
  }
);

export const validateCollectionDateAsync = createAsyncThunk(
  'ui/basketView/validateCollectionDateAsync',
  async ({ values }, { dispatch }) => {
    const collectionCountryCode = get(
      values,
      SHIPMENT_FIELDS.COLLECTION_COUNTRY.KEY
    );
    const collectionPostcode = get(
      values,
      SHIPMENT_FIELDS.COLLECTION_POSTCODE.KEY
    );
    const shipmentDate = get(values, SHIPMENT_FIELDS.SHIPMENT_DATE.KEY);
    const collectionDates = await dispatch(
      fetchCollectionDates({
        countryCode: collectionCountryCode,
        postcode: collectionPostcode,
      })
    ).unwrap();
    const dates = collectionDates.map(({ date, collectionCutOff }) =>
      moment(`${date} ${collectionCutOff}`).format(DATE_TIME_FORMAT)
    );

    return dates.includes(shipmentDate);
  }
);

const slice = createSlice({
  name: 'basketView',
  initialState,
  reducers: {
    // used in pair with prop isStepValues = false
    setCheckoutList(state, { payload }) {
      state.checkoutList = payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(
      fetchBasketItemReferences.fulfilled,
      (state, { payload }) => {
        const [basketItemId, references] = payload;
        state.references[basketItemId] = references;
      }
    );
  },
});

const getBasketReferences = state => state.basketView.references;

const getCheckoutList = state => state.basketView.checkoutList;

const getFirstCheckoutItem = createSelector(
  [getCheckoutList, BasketSelectors.getBasketItems],
  (checkoutList, basketItems) => {
    const firstCheckoutId = checkoutList[0];
    return basketItems.find(
      basketItem => basketItem.basketItemId === firstCheckoutId
    );
  }
);

export const getBillingAddress = createSelector(
  [getFirstCheckoutItem],
  ({ shipment } = {}) => {
    const collectionLocality = get(
      shipment,
      SHIPMENT_FIELDS.COLLECTION_LOCALITY.KEY
    );
    return {
      phoneNumber: get(
        shipment,
        SHIPMENT_FIELDS.COLLECTION_CONTACT_TELEPHONE.KEY
      ),
      streetAddress: get(shipment, SHIPMENT_FIELDS.COLLECTION_STREET.KEY),
      extendedAddress: collectionLocality, // ?
      locality: collectionLocality,
      region: get(shipment, SHIPMENT_FIELDS.COLLECTION_COUNTY.KEY),
      postalCode: get(shipment, SHIPMENT_FIELDS.COLLECTION_POSTCODE.KEY),
      countryCodeAlpha2: get(shipment, SHIPMENT_FIELDS.COLLECTION_COUNTRY.KEY),
    };
  }
);

export const getBillingEmail = createSelector(
  [getFirstCheckoutItem],
  ({ shipment } = {}) =>
    get(shipment, SHIPMENT_FIELDS.COLLECTION_CONTACT_EMAIL.KEY)
);

export const getBillingMobilePhone = createSelector(
  [getFirstCheckoutItem],
  ({ shipment } = {}) =>
    get(shipment, SHIPMENT_FIELDS.COLLECTION_CONTACT_TELEPHONE.KEY)
);

export const getThreeDSecureData = createStructuredSelector({
  email: getBillingEmail,
  mobilePhoneNumber: getBillingMobilePhone,
  billingAddress: getBillingAddress,
});

export default {
  reducer: slice.reducer,
  actions: {
    ...slice.actions,
    fetchBasketItemReferences,
    fetchBasketReferences,
  },
  selectors: {
    getThreeDSecureData,
    getCheckoutList,
    getBasketReferences,
  },
};
